import { OTR_THRESHOLDS } from './OTRDataService';

/**
 * Centralized OTR Data Processing Service
 * Handles all filtering, calculations, and data transformations in one place
 * to eliminate duplication across components and ensure consistency.
 */
class OTRDataProcessor {
  constructor() {
    this.thresholds = OTR_THRESHOLDS;
  }

  /**
   * Get unique trip identifier from a record
   * New format: ROUTE_VARIANT_STARTTIME_CONTRACT_CODE
   * Falls back to legacy formats for backwards compatibility
   * @param {Object} record - Data record
   * @returns {string} Unique trip identifier
   */
  getTripKey(record) {
    let key = record.KEY;
    
    // Debug logging for specific trip IDs
    if (record.TRIP_ID === '100081' || record.TRIP_ID === '100049') {
      console.log(`getTripKey debug for TRIP_ID ${record.TRIP_ID}:`, {
        KEY: record.KEY,
        TRIP_ID: record.TRIP_ID,
        DATE_ID: record.DATE_ID,
        ROUTE_VARIANT: record.ROUTE_VARIANT,
        ROUTE: record.ROUTE
      });
    }
    
    // If no KEY field, fall back to legacy TRIP_ID for backwards compatibility
    if (!key) {
      key = record.TRIP_ID;
      if (record.TRIP_ID === '100081' || record.TRIP_ID === '100049') {
        console.log(`getTripKey: No KEY field, falling back to TRIP_ID: ${key}`);
      }
    }
    
    // Handle legacy keys with "undefined" route variant (old format)
    if (typeof key === 'string' && key.startsWith('undefined_')) {
      // Try to reconstruct the key using available route information
      const route = record.ROUTE_VARIANT || record.ROUTE;
      if (route) {
        const newKey = key.replace('undefined_', `${route}_`);
        if (record.TRIP_ID === '100081' || record.TRIP_ID === '100049') {
          console.log(`getTripKey: Reconstructed key from ${key} to ${newKey}`);
        }
        key = newKey;
      }
    }
    
    if (record.TRIP_ID === '100081' || record.TRIP_ID === '100049') {
      console.log(`getTripKey: Final key for TRIP_ID ${record.TRIP_ID} = ${key}`);
    }
    
    return key;
  }

  /**
   * Get departure variance from a stop record
   * @param {Object} stop - Stop record
   * @returns {number|null} Variance in seconds
   */
  getDepartureVariance(stop) {
    if (Number.isFinite(stop.DEPARTURE_TIME_VARIANCE)) {
      return stop.DEPARTURE_TIME_VARIANCE;
    } else if (Number.isFinite(stop.A_DEP) && Number.isFinite(stop.S_DEP)) {
      return stop.A_DEP - stop.S_DEP;
    } else if (Number.isFinite(stop.ARRIVAL_TIME_VARIANCE)) {
      // Fallback to arrival variance if that's all we have
      return stop.ARRIVAL_TIME_VARIANCE;
    }
    return null;
  }

  /**
   * Check if a stop should have early treated as on-time (school rule)
   * @param {Object} stop - Stop record
   * @param {boolean} treatSchoolOutEarlyAsOnTime - Whether the rule is enabled
   * @returns {boolean}
   */
  shouldTreatEarlyAsOnTime(stop, treatSchoolOutEarlyAsOnTime) {
    if (!treatSchoolOutEarlyAsOnTime) return false;
    
    // Check if this is an "Out" direction
    const isOutDirection = stop.DIRECTION && (
      String(stop.DIRECTION).toLowerCase().includes('out') ||
      String(stop.DIRECTION).toLowerCase().includes('outbound') ||
      String(stop.DIRECTION) === '1' // Sometimes outbound is coded as 1
    );
    
    // Check if this is a school run
    const isSchoolRun = stop.ROUTE_TYPE_NAME && (
      String(stop.ROUTE_TYPE_NAME).toLowerCase().includes('school') ||
      String(stop.ROUTE_TYPE_NAME).toLowerCase().includes('education')
    );
    
    return isOutDirection && isSchoolRun;
  }

  /**
   * Get on-time status for a stop
   * @param {number} variance - Variance in seconds
   * @param {Object} stop - Stop record (optional, for school rule)
   * @param {boolean} treatSchoolOutEarlyAsOnTime - Whether school rule is enabled
   * @returns {string} 'ON_TIME', 'EARLY', 'LATE', or 'UNKNOWN'
   */
  getOnTimeStatus(variance, stop = null, treatSchoolOutEarlyAsOnTime = false) {
    if (variance === undefined || variance === null) return 'UNKNOWN';
    
    const isNormallyOnTime = variance > this.thresholds.early && variance < this.thresholds.late;
    if (isNormallyOnTime) return 'ON_TIME';
    
    const isEarly = variance <= this.thresholds.early;
    if (isEarly) {
      // Check if this early should be treated as on-time for school out runs
      if (stop && this.shouldTreatEarlyAsOnTime(stop, treatSchoolOutEarlyAsOnTime)) {
        return 'ON_TIME';
      }
      return 'EARLY';
    }
    
    return 'LATE';
  }

  /**
   * Calculate statistics for a set of stops
   * @param {Array} stops - Array of stop records
   * @param {boolean} treatSchoolOutEarlyAsOnTime - Whether school rule is enabled
   * @returns {Object} Statistics object
   */
  calculateStopStats(stops, treatSchoolOutEarlyAsOnTime = false) {
    if (!stops || stops.length === 0) {
      return {
        onTimePercent: 0,
        earlyPercent: 0, 
        latePercent: 0,
        earlyCount: 0,
        onTimeCount: 0,
        lateCount: 0,
        count: 0,
        avgVariance: 0
      };
    }

    // Prioritise departure variance for consistency
    const stopsWithVariance = stops.filter(s => this.getDepartureVariance(s) !== null);
    
    if (stopsWithVariance.length === 0) {
      return {
        onTimePercent: 0,
        earlyPercent: 0,
        latePercent: 0,
        earlyCount: 0,
        onTimeCount: 0,
        lateCount: 0,
        count: stops.length,
        avgVariance: 0
      };
    }

    let onTimeCount = 0;
    let earlyCount = 0;
    let lateCount = 0;
    let totalVariance = 0;

    stopsWithVariance.forEach(stop => {
      const variance = this.getDepartureVariance(stop);
      totalVariance += variance;

      const status = this.getOnTimeStatus(variance, stop, treatSchoolOutEarlyAsOnTime);
      
      switch (status) {
        case 'ON_TIME':
          onTimeCount++;
          break;
        case 'EARLY':
          earlyCount++;
          break;
        case 'LATE':
          lateCount++;
          break;
      }
    });

    const avgVariance = totalVariance / stopsWithVariance.length;

    return {
      onTimePercent: (onTimeCount / stopsWithVariance.length) * 100,
      earlyPercent: (earlyCount / stopsWithVariance.length) * 100,
      latePercent: (lateCount / stopsWithVariance.length) * 100,
      earlyCount,
      onTimeCount,
      lateCount,
      count: stops.length,
      avgVariance
    };
  }

  /**
   * Filter data by stop types
   * @param {Array} data - Raw stop data
   * @param {Array} selectedStopTypes - Selected stop types ['F', 'M', 'L', 'ALL']
   * @returns {Array} Filtered data
   */
  filterDataByStopType(data, selectedStopTypes) {
    if (!data || !Array.isArray(data) || data.length === 0) return [];
    
    // If no stop types are selected, return all data
    if (!selectedStopTypes || selectedStopTypes.length === 0) return data;
    
    // If 'ALL' is selected, return all data
    if (selectedStopTypes.includes('ALL')) return data;
    
    // DEBUG: Log filtering details
    console.log('=== FILTER DEBUG ===');
    console.log('Input data length:', data.length);
    console.log('selectedStopTypes:', selectedStopTypes);
    
    // Check what stop types exist in the data
    const stopTypeCounts = {};
    data.forEach(item => {
      const stopType = item.TSN_FIRST_LAST_IND;
      stopTypeCounts[stopType] = (stopTypeCounts[stopType] || 0) + 1;
    });
    console.log('Stop type distribution:', stopTypeCounts);
    
    // Filter data based on selected stop types
    const filtered = data.filter(item => {
      const stopType = item.TSN_FIRST_LAST_IND;
      return selectedStopTypes.includes(stopType);
    });
    
    console.log('Filtered data length:', filtered.length);
    console.log('=== END FILTER DEBUG ===');
    
    return filtered;
  }

  /**
   * Process raw data into trip-level aggregations
   * @param {Array} data - Raw stop data
   * @param {boolean} treatSchoolOutEarlyAsOnTime - Whether school rule is enabled
   * @returns {Array} Processed trip data
   */
  processTripsData(data, treatSchoolOutEarlyAsOnTime = false) {
    // Group data by trip using unique KEY
    const tripGroups = {};
    
    console.log(`Processing ${data.length} stop records into trips...`);
    
    data.forEach(record => {
      const tripKey = this.getTripKey(record);
      if (!tripKey) return;
      
      if (!tripGroups[tripKey]) {
        tripGroups[tripKey] = {
          tripKey: tripKey,
          tripId: record.TRIP_ID, // Keep original TRIP_ID for display
          route: record.ROUTE,
          stops: [],
          firstStops: [],
          midStops: [],
          lastStops: [],
          dates: new Set(),
          driverId: record.DRIVER_ID,
          depot: record.DEPOT,
          operatorId: record.OPERATOR_ID,
          contract: record.CONTRACT
        };
        console.log(`Created new trip group for KEY: ${tripKey} (TRIP_ID: ${record.TRIP_ID})`);
      }
      
      // Add to appropriate stop category
      if (record.TSN_FIRST_LAST_IND === 'F') {
        tripGroups[tripKey].firstStops.push(record);
      } else if (!record.TSN_FIRST_LAST_IND?.length || record.TSN_FIRST_LAST_IND === 'M') {
        tripGroups[tripKey].midStops.push(record);
      } else if (record.TSN_FIRST_LAST_IND === 'L') {
        tripGroups[tripKey].lastStops.push(record);
      } else {
        tripGroups[tripKey].midStops.push(record);
      }
      
      // Add to all stops
      tripGroups[tripKey].stops.push(record);
      
      // Track unique dates
      if (record.DATE_ID) {
        tripGroups[tripKey].dates.add(record.DATE_ID);
      }
    });
    
    // Calculate stats for each trip and convert to array
    const processedTrips = Object.values(tripGroups).map(trip => {
      // Calculate performance metrics
      const firstStopStats = this.calculateStopStats(trip.firstStops, treatSchoolOutEarlyAsOnTime);
      const midStopStats = this.calculateStopStats(trip.midStops, treatSchoolOutEarlyAsOnTime);
      const lastStopStats = this.calculateStopStats(trip.lastStops, treatSchoolOutEarlyAsOnTime);
      const overallStats = this.calculateStopStats(trip.stops, treatSchoolOutEarlyAsOnTime);
      
      return {
        tripKey: trip.tripKey,
        tripId: trip.tripId, // Keep original TRIP_ID for display
        route: trip.route,
        dateCount: trip.dates.size,
        dates: Array.from(trip.dates),
        stopCount: trip.stops.length,
        firstStopStats,
        midStopStats,
        lastStopStats,
        overallStats,
        driverId: trip.driverId,
        depot: trip.depot,
        operatorId: trip.operatorId,
        contract: trip.contract,
        recordCount: trip.stops.length
      };
    });
    
    console.log(`Processed ${Object.keys(tripGroups).length} unique trips from ${data.length} stop records`);
    console.log('Trip KEYs:', Object.keys(tripGroups));
    
    return processedTrips;
  }

  /**
   * Calculate aggregated statistics for overview displays
   * @param {Array} stopData - Raw stop data
   * @param {Array} selectedStopTypes - Selected stop types
   * @param {boolean} treatSchoolOutEarlyAsOnTime - Whether school rule is enabled
   * @returns {Object} Aggregated statistics
   */
  calculateAggregatedStats(stopData, selectedStopTypes, treatSchoolOutEarlyAsOnTime = false) {
    // Filter the data by selected stop types
    const filteredStopData = this.filterDataByStopType(stopData, selectedStopTypes);

    if (!filteredStopData || filteredStopData.length === 0) {
      return {
        onTimePercent: 0,
        earlyPercent: 0,
        latePercent: 0,
        total: 0,
        stopsWithVariance: 0,
        stopsWithoutVariance: 0
      };
    }

    let onTimeCount = 0;
    let earlyCount = 0;
    let lateCount = 0;
    let totalCount = 0;
    let stopsWithVariance = 0;
    let stopsWithoutVariance = 0;

    filteredStopData.forEach(record => {
      totalCount++;
      
      const variance = this.getDepartureVariance(record);
      
      if (variance !== null) {
        stopsWithVariance++;
        
        const status = this.getOnTimeStatus(variance, record, treatSchoolOutEarlyAsOnTime);
        
        switch (status) {
          case 'ON_TIME':
            onTimeCount++;
            break;
          case 'EARLY':
            earlyCount++;
            break;
          case 'LATE':
            lateCount++;
            break;
        }
      } else {
        stopsWithoutVariance++;
      }
    });

    const validCount = stopsWithVariance > 0 ? stopsWithVariance : totalCount;

    return {
      onTimePercent: validCount > 0 ? (onTimeCount / validCount) * 100 : 0,
      earlyPercent: validCount > 0 ? (earlyCount / validCount) * 100 : 0,
      latePercent: validCount > 0 ? (lateCount / validCount) * 100 : 0,
      total: totalCount,
      stopsWithVariance: stopsWithVariance,
      stopsWithoutVariance: stopsWithoutVariance,
      onTimeCount,
      earlyCount,
      lateCount
    };
  }

  /**
   * Calculate daily performance statistics
   * @param {Array} stopData - Raw stop data
   * @param {Array} selectedStopTypes - Selected stop types
   * @param {boolean} treatSchoolOutEarlyAsOnTime - Whether school rule is enabled
   * @returns {Object} Daily performance data
   */
  calculateDailyPerformance(stopData, selectedStopTypes, treatSchoolOutEarlyAsOnTime = false) {
    const dailyOTR = {};
    
    // Process all stop data (not filtered by stop type yet) to get stop-type-specific breakdowns
    stopData.forEach(record => {
      const dateId = record.DATE_ID;
      if (!dateId) return;
      
      const dateStr = String(dateId);
      if (!dailyOTR[dateStr]) {
        dailyOTR[dateStr] = {
          date: dateStr,
          displayDate: record.DATE_FORMATTED || new Date(dateStr).toISOString().split('T')[0],
          totalCount: 0,
          onTimeStops: 0,
          earlyStops: 0,
          lateStops: 0,
          stopsWithVariance: 0,
          // Stop-type-specific breakdowns
          firstStops: { total: 0, onTime: 0, early: 0, late: 0 },
          midStops: { total: 0, onTime: 0, early: 0, late: 0 },
          lastStops: { total: 0, onTime: 0, early: 0, late: 0 }
        };
      }
      
      const dayData = dailyOTR[dateStr];
      dayData.totalCount++;
      
      const variance = this.getDepartureVariance(record);
      
      if (variance !== null) {
        dayData.stopsWithVariance++;
        
        const status = this.getOnTimeStatus(variance, record, treatSchoolOutEarlyAsOnTime);
        
        // Update overall counts
        switch (status) {
          case 'ON_TIME':
            dayData.onTimeStops++;
            break;
          case 'EARLY':
            dayData.earlyStops++;
            break;
          case 'LATE':
            dayData.lateStops++;
            break;
        }
        
        // Update stop-type-specific counts
        const stopType = record.TSN_FIRST_LAST_IND;
        let targetStats = null;
        
        if (stopType === 'F') {
          targetStats = dayData.firstStops;
        } else if (stopType === 'M' || !stopType) {
          targetStats = dayData.midStops;
        } else if (stopType === 'L') {
          targetStats = dayData.lastStops;
        }
        
        if (targetStats) {
          targetStats.total++;
          switch (status) {
            case 'ON_TIME':
              targetStats.onTime++;
              break;
            case 'EARLY':
              targetStats.early++;
              break;
            case 'LATE':
              targetStats.late++;
              break;
          }
        }
      }
    });
    
    // Calculate percentages
    Object.values(dailyOTR).forEach(day => {
      // Overall percentages (based on stops with variance)
      const validStops = day.stopsWithVariance;
      if (validStops > 0) {
        day.onTimePercent = (day.onTimeStops / validStops) * 100;
        day.earlyPercent = (day.earlyStops / validStops) * 100;
        day.latePercent = (day.lateStops / validStops) * 100;
      } else {
        day.onTimePercent = 0;
        day.earlyPercent = 0;
        day.latePercent = 0;
      }
      
      // Stop-type-specific percentages
      ['firstStops', 'midStops', 'lastStops'].forEach(stopType => {
        const stats = day[stopType];
        if (stats.total > 0) {
          stats.onTimePercent = (stats.onTime / stats.total) * 100;
          stats.earlyPercent = (stats.early / stats.total) * 100;
          stats.latePercent = (stats.late / stats.total) * 100;
        } else {
          stats.onTimePercent = 0;
          stats.earlyPercent = 0;
          stats.latePercent = 0;
        }
      });
    });
    
    return dailyOTR;
  }

  /**
   * Calculate stop-level statistics for map displays
   * @param {Array} stopData - Raw stop data
   * @param {boolean} treatSchoolOutEarlyAsOnTime - Whether school rule is enabled
   * @returns {Object} Stop statistics
   */
  calculateStopStatistics(stopData, treatSchoolOutEarlyAsOnTime = false) {
    const stopStats = {};
    
    stopData.forEach(stop => {
      const tsn = stop.TSN;
      if (!tsn) return;
      
      if (!stopStats[tsn]) {
        stopStats[tsn] = {
          tsn: tsn,
          stopName: stop.STOP_NAME || `Stop ${tsn}`,
          latitude: stop.LATITUDE,
          longitude: stop.LONGITUDE,
          departures: 0,
          earlyCount: 0,
          onTimeCount: 0,
          lateCount: 0,
          unknownCount: 0,
          totalCount: 0,
          hasFirstStopRole: false,
          hasMidStopRole: false,
          hasLastStopRole: false,
          firstStopStats: { earlyCount: 0, onTimeCount: 0, lateCount: 0, total: 0 },
          midStopStats: { earlyCount: 0, onTimeCount: 0, lateCount: 0, total: 0 },
          lastStopStats: { earlyCount: 0, onTimeCount: 0, lateCount: 0, total: 0 },
          routes: new Set(),
          trips: new Set(), // Track unique trips for this stop
          averageVariance: 0,
          totalVariance: 0,
          varianceCount: 0
        };
      }
      
      const stopStat = stopStats[tsn];
      
      // Track role flags
      if (stop.TSN_FIRST_LAST_IND === 'F') {
        stopStat.hasFirstStopRole = true;
      } else if (stop.TSN_FIRST_LAST_IND === 'M') {
        stopStat.hasMidStopRole = true;
      } else if (stop.TSN_FIRST_LAST_IND === 'L') {
        stopStat.hasLastStopRole = true;
      }
      
      stopStat.departures++;
      stopStat.totalCount++;
      
      if (stop.ROUTE) {
        stopStat.routes.add(stop.ROUTE);
      }
      
      // Track unique trips for this stop
      if (stop.TRIP_ID) {
        stopStat.trips.add(stop.TRIP_ID);
      }
      
      const variance = this.getDepartureVariance(stop);
      
      if (variance !== null) {
        stopStat.totalVariance += variance;
        stopStat.varianceCount++;
        
        const status = this.getOnTimeStatus(variance, stop, treatSchoolOutEarlyAsOnTime);
        
        // Update overall counts
        switch (status) {
          case 'ON_TIME':
            stopStat.onTimeCount++;
            break;
          case 'EARLY':
            stopStat.earlyCount++;
            break;
          case 'LATE':
            stopStat.lateCount++;
            break;
        }
        
        // Update stop type specific stats
        const stopTypeStats = stop.TSN_FIRST_LAST_IND === 'F' ? stopStat.firstStopStats :
                            stop.TSN_FIRST_LAST_IND === 'M' ? stopStat.midStopStats :
                            stop.TSN_FIRST_LAST_IND === 'L' ? stopStat.lastStopStats : null;
        
        if (stopTypeStats) {
          stopTypeStats.total++;
          switch (status) {
            case 'ON_TIME':
              stopTypeStats.onTimeCount++;
              break;
            case 'EARLY':
              stopTypeStats.earlyCount++;
              break;
            case 'LATE':
              stopTypeStats.lateCount++;
              break;
          }
        }
      } else {
        stopStat.unknownCount++;
      }
    });
    
    // Calculate percentages
    Object.values(stopStats).forEach(stop => {
      const validRecords = stop.earlyCount + stop.onTimeCount + stop.lateCount;
      stop.earlyPercent = validRecords > 0 ? (stop.earlyCount / validRecords) * 100 : 0;
      stop.onTimePercent = validRecords > 0 ? (stop.onTimeCount / validRecords) * 100 : 0;
      stop.latePercent = validRecords > 0 ? (stop.lateCount / validRecords) * 100 : 0;
      stop.otrPercent = stop.onTimePercent; // Alias for compatibility
      
      // Calculate percentages for stop types
      ['firstStopStats', 'midStopStats', 'lastStopStats'].forEach(statType => {
        const stats = stop[statType];
        if (stats.total > 0) {
          stats.onTimePercent = (stats.onTimeCount / stats.total) * 100;
          stats.earlyPercent = (stats.earlyCount / stats.total) * 100;
          stats.latePercent = (stats.lateCount / stats.total) * 100;
        } else {
          stats.onTimePercent = 0;
          stats.earlyPercent = 0;
          stats.latePercent = 0;
        }
      });
      
      stop.averageVariance = stop.varianceCount > 0 ? stop.totalVariance / stop.varianceCount : 0;
      stop.routeCount = stop.routes.size;
      stop.tripCount = stop.trips.size; // Pre-calculated trip count
    });
    
    return stopStats;
  }

  /**
   * Process data for driver performance analysis
   * @param {Array} tripData - Processed trip data
   * @param {Array} stopData - Raw stop data
   * @param {boolean} treatSchoolOutEarlyAsOnTime - Whether school rule is enabled
   * @returns {Array} Driver performance data
   */
  processDriverData(tripData, stopData, treatSchoolOutEarlyAsOnTime = false) {
    const driverGroups = {};
    
    // Group trips by driver
    tripData.forEach(trip => {
      if (!trip.driverId) return;
      
      if (!driverGroups[trip.driverId]) {
        driverGroups[trip.driverId] = {
          driverId: trip.driverId,
          trips: [],
          totalStops: 0,
          onTimeStops: 0,
          earlyStops: 0,
          lateStops: 0
        };
      }
      
      driverGroups[trip.driverId].trips.push(trip);
      driverGroups[trip.driverId].totalStops += trip.overallStats.count;
      driverGroups[trip.driverId].onTimeStops += trip.overallStats.onTimeCount;
      driverGroups[trip.driverId].earlyStops += trip.overallStats.earlyCount;
      driverGroups[trip.driverId].lateStops += trip.overallStats.lateCount;
    });
    
    // Calculate driver statistics
    return Object.values(driverGroups).map(driver => {
      const validStops = driver.onTimeStops + driver.earlyStops + driver.lateStops;
      
      return {
        driverId: driver.driverId,
        tripCount: driver.trips.length,
        totalStops: driver.totalStops,
        onTimePercent: validStops > 0 ? (driver.onTimeStops / validStops) * 100 : 0,
        earlyPercent: validStops > 0 ? (driver.earlyStops / validStops) * 100 : 0,
        latePercent: validStops > 0 ? (driver.lateStops / validStops) * 100 : 0,
        onTimeCount: driver.onTimeStops,
        earlyCount: driver.earlyStops,
        lateCount: driver.lateStops
      };
    });
  }
}

// Export singleton instance
const otrDataProcessor = new OTRDataProcessor();
export default otrDataProcessor; 